import type { CountryConfig } from './types'
import { usaTaxStrategy } from './strategies/usaTaxStrategy'
import { usaAssetStrategy } from './strategies/usaAssetStrategy'

export const usaConfig: CountryConfig = {
  code: 'USA',
  name: 'United States',
  flag: '🇺🇸',

  currency: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US',
    defaultExchangeRate: 90.0,
    decimalPlaces: 0,
  },

  taxRules: {
    hasIncomeTax: true,
    brackets: [
      { min: 0, max: 11600, rate: 0.10, label: '10% bracket' },
      { min: 11600, max: 47150, rate: 0.12, label: '12% bracket' },
      { min: 47150, max: 100525, rate: 0.22, label: '22% bracket' },
      { min: 100525, max: 191950, rate: 0.24, label: '24% bracket' },
      { min: 191950, max: 243725, rate: 0.32, label: '32% bracket' },
      { min: 243725, max: 609350, rate: 0.35, label: '35% bracket' },
      { min: 609350, max: null, rate: 0.37, label: '37% bracket' },
    ],
    ltcgRate: 0.15,
    stcgRate: 0.24,
    ltcgThresholdDays: 365,
    exemptions: [
      {
        id: 'primary-home-single',
        name: 'Primary Home Exclusion (Single)',
        amount: 250000,
        description: '$250K capital gains exclusion for primary residence',
        conditions: ['Owned and lived in home 2+ years', 'Single filer']
      },
      {
        id: 'primary-home-married',
        name: 'Primary Home Exclusion (Married)',
        amount: 500000,
        description: '$500K capital gains exclusion for primary residence',
        conditions: ['Owned and lived in home 2+ years', 'Married filing jointly']
      },
    ],
    dtaaBenefits: [
      {
        id: 'pension',
        article: 'Article 20',
        description: 'Pension income taxed only in country of residence',
        applicableTo: ['socialSecurity', '401k', 'ira']
      },
      {
        id: 'capital-gains',
        article: 'Article 13',
        description: 'Capital gains on movable property taxed in resident country',
        applicableTo: ['taxableBrokerage', 'crypto']
      },
    ],
    requiredForms: [
      {
        id: 'form-8854',
        name: 'Form 8854',
        description: 'Expatriation Statement - Required when giving up US citizenship or long-term residency',
        required: true,
        deadline: 'With tax return for year of expatriation',
        link: 'https://www.irs.gov/forms-pubs/about-form-8854'
      },
      {
        id: 'fbar',
        name: 'FBAR (FinCEN 114)',
        description: 'Foreign Bank Account Report - Required if foreign accounts exceed $10,000 at any time',
        required: true,
        deadline: 'April 15 (auto-extended to October 15)',
        link: 'https://bsaefiling.fincen.treas.gov/'
      },
      {
        id: 'form-8938',
        name: 'Form 8938 (FATCA)',
        description: 'Statement of Specified Foreign Financial Assets',
        required: false,
        deadline: 'With tax return',
      },
    ],
    specialRules: {
      'early-withdrawal': '10% penalty for retirement account withdrawals before age 59.5',
      'rmd': 'Required Minimum Distributions start at age 73',
      'roth-conversion': 'Can convert Traditional IRA to Roth, taxed as ordinary income',
    },
  },

  assets: {
    retirement: [
      {
        id: '401k',
        name: '401(k)',
        description: 'Employer-sponsored retirement plan',
        icon: '🏦',
        category: 'retirement',
        taxTreatment: 'tax-deferred',
        enabled: true,
        primary: true,
        fieldMapping: 'balance401k',
        helpText: 'Pre-tax retirement account with employer match. Taxed on withdrawal.',
        sortOrder: 1,
        withdrawalRules: [
          { minAge: 59.5, penaltyRate: 0, description: 'Penalty-free after 59.5' },
          { minAge: 55, maxAge: 59.5, penaltyRate: 0, description: 'Rule of 55: Penalty-free if separated from employer after 55' },
          { minAge: 0, maxAge: 55, penaltyRate: 0.10, description: '10% early withdrawal penalty' },
        ],
        contributionLimits: [
          { year: 2024, limit: 23000, catchUpAge: 50, catchUpLimit: 7500 },
          { year: 2025, limit: 23500, catchUpAge: 50, catchUpLimit: 7500 },
        ],
      },
      {
        id: 'rothIRA',
        name: 'Roth IRA',
        description: 'After-tax retirement account with tax-free growth',
        icon: '💎',
        category: 'retirement',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: true,
        fieldMapping: 'balanceRothIRA',
        helpText: 'Contributions are after-tax, but growth and qualified withdrawals are tax-free.',
        sortOrder: 2,
        withdrawalRules: [
          { minAge: 59.5, penaltyRate: 0, description: 'Qualified distributions are tax-free' },
          { minAge: 0, maxAge: 59.5, penaltyRate: 0.10, description: '10% penalty on earnings (contributions always penalty-free)' },
        ],
        contributionLimits: [
          { year: 2024, limit: 7000, catchUpAge: 50, catchUpLimit: 1000 },
          { year: 2025, limit: 7000, catchUpAge: 50, catchUpLimit: 1000 },
        ],
      },
      {
        id: 'traditionalIRA',
        name: 'Traditional IRA',
        description: 'Tax-deferred individual retirement account',
        icon: '📊',
        category: 'retirement',
        taxTreatment: 'tax-deferred',
        enabled: true,
        primary: false,
        fieldMapping: 'balanceTraditionalIRA',
        helpText: 'Contributions may be tax-deductible. Taxed as ordinary income on withdrawal.',
        sortOrder: 3,
        withdrawalRules: [
          { minAge: 59.5, penaltyRate: 0, description: 'Penalty-free after 59.5' },
          { minAge: 0, maxAge: 59.5, penaltyRate: 0.10, description: '10% early withdrawal penalty' },
        ],
        contributionLimits: [
          { year: 2024, limit: 7000, catchUpAge: 50, catchUpLimit: 1000 },
        ],
      },
      {
        id: 'hsa',
        name: 'HSA',
        description: 'Health Savings Account',
        icon: '🏥',
        category: 'retirement',
        taxTreatment: 'tax-free',
        enabled: true,
        primary: false,
        fieldMapping: 'balanceHSA',
        helpText: 'Triple tax-advantaged: tax-deductible contributions, tax-free growth, tax-free withdrawals for medical expenses.',
        sortOrder: 4,
        withdrawalRules: [
          { minAge: 65, penaltyRate: 0, description: 'Can use for any purpose after 65 (taxed as income if non-medical)' },
          { minAge: 0, maxAge: 65, penaltyRate: 0.20, description: '20% penalty for non-medical withdrawals before 65' },
        ],
        contributionLimits: [
          { year: 2024, limit: 4150, catchUpAge: 55, catchUpLimit: 1000 }, // Self-only
        ],
      },
    ],
    investment: [
      {
        id: 'taxableBrokerage',
        name: 'Taxable Brokerage',
        description: 'Regular investment account (stocks, ETFs, bonds)',
        icon: '📈',
        category: 'investment',
        taxTreatment: 'taxable',
        enabled: true,
        primary: true,
        fieldMapping: 'taxableBrokerage',
        helpText: 'No contribution limits. Gains taxed at LTCG/STCG rates.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: 'crypto',
        name: 'Cryptocurrency',
        description: 'Bitcoin, Ethereum, and other digital assets',
        icon: '₿',
        category: 'investment',
        taxTreatment: 'taxable',
        enabled: true,
        primary: false,
        fieldMapping: 'crypto',
        helpText: 'Taxed as property in USA. Note: 30% flat tax on crypto gains in India.',
        sortOrder: 2,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: '529Plan',
        name: '529 Plan',
        description: 'Tax-advantaged education savings',
        icon: '🎓',
        category: 'investment',
        taxTreatment: 'tax-deferred',
        enabled: true,
        primary: false,
        fieldMapping: 'balance529Plan',
        helpText: 'Tax-free growth for qualified education expenses. Can now rollover to Roth IRA.',
        sortOrder: 3,
        withdrawalRules: [
          { minAge: 0, penaltyRate: 0.10, description: '10% penalty on earnings for non-education withdrawals' },
        ],
        contributionLimits: [],
      },
    ],
    employerEquity: [
      {
        id: 'rsu',
        name: 'RSU / Stock Awards',
        description: 'Restricted Stock Units and company stock',
        icon: '🏢',
        category: 'employerEquity',
        taxTreatment: 'taxable',
        enabled: true,
        primary: true,
        fieldMapping: 'rsuEquityValue',
        helpText: 'RSUs taxed as ordinary income at vesting. Further gains taxed as capital gains when sold.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: 'espp',
        name: 'ESPP Shares',
        description: 'Employee Stock Purchase Plan shares',
        icon: '📈',
        category: 'employerEquity',
        taxTreatment: 'complex',
        enabled: true,
        primary: true, // Made primary so users can see/edit ESPP value
        fieldMapping: 'esppBalance',
        helpText: 'ESPP discount portion taxed as ordinary income. Hold 2+ years for qualified disposition.',
        sortOrder: 2,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],
    realEstate: [
      {
        id: 'primaryHome',
        name: 'Primary Home',
        description: 'Primary residence (equity value)',
        icon: '🏠',
        category: 'realEstate',
        taxTreatment: 'complex',
        enabled: true,
        primary: false,
        fieldMapping: 'primaryHomeEquity',
        helpText: 'Enter equity (value minus mortgage). $250K/$500K capital gains exclusion if lived 2+ years.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
      {
        id: 'rentalProperty',
        name: 'Rental Property',
        description: 'Investment property (equity value)',
        icon: '🏘️',
        category: 'realEstate',
        taxTreatment: 'taxable',
        enabled: true,
        primary: false,
        fieldMapping: 'rentalPropertyEquity',
        helpText: 'Enter equity (value minus mortgage). Consider 1031 exchange or keeping for passive income.',
        sortOrder: 2,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],
    cash: [
      {
        id: 'savingsChecking',
        name: 'Savings & Checking',
        description: 'Bank accounts',
        icon: '💵',
        category: 'cash',
        taxTreatment: 'taxable',
        enabled: true,
        primary: false,
        fieldMapping: 'savingsChecking',
        helpText: 'FBAR reporting required if total foreign accounts exceed $10K at any time.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],
    benefits: [
      {
        id: 'socialSecurity',
        name: 'Social Security',
        description: 'Expected monthly benefit',
        icon: '🏛️',
        category: 'benefits',
        taxTreatment: 'partially-taxable',
        enabled: true,
        primary: false,
        fieldMapping: 'socialSecurityMonthlyBenefit',
        helpText: 'Requires 40 quarters of work history. Can receive in India if eligible.',
        sortOrder: 1,
        withdrawalRules: [],
        contributionLimits: [],
      },
    ],
  },

  benefits: {
    hasSocialSecurity: true,
    socialSecurityRules: {
      minQuarters: 40,
      canReceiveAbroad: true,
      reductionIfEarly: 6.67, // Per year before full retirement age
    },
    hasEOSB: false,
  },

  labels: {
    retirementSection: 'Retirement Accounts',
    investmentSection: 'Investments',
    employerEquitySection: 'Employer Equity',
    realEstateSection: 'Real Estate',
    cashSection: 'Cash & Bank Accounts',
    benefitsSection: 'Benefits',
    incomeLabel: 'Annual Income (USD)',
    expenseLabel: 'Monthly Expenses (USD)',
    savingsLabel: 'Retirement Savings',
    netWorthLabel: 'Net Worth',
    taxImplicationsLabel: 'Tax Implications',
    capitalGainsLabel: 'Capital Gains Tax',
  },

  helpText: {
    retirementAccounts: 'Tax-advantaged accounts like 401(k), IRA, and HSA. Penalties may apply for early withdrawal.',
    taxImplications: 'Consider DTAA benefits when repatriating funds. Plan liquidation timing for tax efficiency.',
    dtaaBenefits: 'USA-India Double Tax Avoidance Agreement may reduce tax burden on certain income types.',
    withdrawalPenalties: '10% early withdrawal penalty applies to most retirement accounts before age 59.5.',
  },

  taxStrategy: usaTaxStrategy,
  assetStrategy: usaAssetStrategy,
}
